import { motion } from "framer-motion";
import { STATS } from "../../lib/data";
import NumberCounter from "../NumberCounter";
import { usePageContent } from "../../lib/usePageContent";
import { useStats } from "../../lib/useStats";

const DEFAULTS = {
  intro_eyebrow: "Invest Hospitality",
  intro_lines: [
    "Sviluppiamo hotel.",
    "Li costruiamo.",
    "Li gestiamo direttamente.",
    "A Milano. Dal 2014.",
  ],
  intro_paragraph_1:
    "Siamo una holding italiana indipendente. Dal 2014 selezioniamo immobili strategici e li trasformiamo in strutture ricettive moderne, seguendo ogni fase: dalla ricerca della location ai permessi, dalla realizzazione alla gestione diretta.",
  intro_paragraph_2:
    "La nostra missione è generare valore sostenibile per gli asset e garantire un'accoglienza di alto livello, perfettamente inserita nel contesto urbano.",
  stat_hotels_label: "Hotel attivi",
  stat_rooms_label: "Camere",
  stat_pipeline_hotels_label: "In pipeline",
  stat_pipeline_rooms_label: "Camere in arrivo",
};

export default function IntroSection() {
  const c = usePageContent("home", DEFAULTS);
  const stats = useStats();
  const lines = Array.isArray(c.intro_lines) && c.intro_lines.length
    ? c.intro_lines
    : DEFAULTS.intro_lines;
  return (
    <section
      data-testid="intro-section"
      className="relative py-32 md:py-48 px-6 md:px-12 max-w-[1440px] mx-auto"
    >
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-7">
          <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-white/50">
            <span className="h-px w-8 bg-[#C48F56]" />
            {c.intro_eyebrow}
          </div>
          <h2 className="mt-6 font-display text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight font-medium">
            {lines.map((l, i) => (
              <motion.span
                key={`${i}-${l}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.05 }}
                transition={{
                  duration: 0.8,
                  delay: i * 0.08,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className="block"
              >
                {i === lines.length - 1 ? (
                  <span className="text-[#C48F56]">{l}</span>
                ) : (
                  l
                )}
              </motion.span>
            ))}
          </h2>
        </div>

        <div className="lg:col-span-5 lg:pt-20">
          <p className="text-white/70 text-lg leading-relaxed">
            {c.intro_paragraph_1}
          </p>
          <p className="mt-5 text-white/50 leading-relaxed">
            {c.intro_paragraph_2}
          </p>

          <div className="mt-10 grid grid-cols-2 gap-5">
            <Stat label={c.stat_hotels_label} value={stats?.hotels ?? STATS.hotels} />
            <Stat label={c.stat_rooms_label} value={stats?.rooms ?? STATS.rooms} />
            <Stat label={c.stat_pipeline_hotels_label} value={stats?.pipeline_hotels ?? STATS.pipelineHotels} />
            <Stat label={c.stat_pipeline_rooms_label} value={stats?.pipeline_rooms ?? STATS.pipelineRooms} prefix="+" />
          </div>

          {/* Target 2027 — total rooms after pipeline completion */}
          <div
            data-testid="intro-target-2027"
            className="mt-8 rounded-2xl border border-[#C48F56]/30 bg-gradient-to-br from-[#C48F56]/8 to-transparent p-6"
          >
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#C48F56]">
              {c.target_label || "Traguardo 2027"}
            </div>
            <div className="mt-2 flex items-baseline gap-3">
              <NumberCounter
                value={(stats?.rooms ?? STATS.rooms) + (stats?.pipeline_rooms ?? STATS.pipelineRooms)}
                className="font-display text-5xl md:text-6xl tracking-tight leading-none"
              />
              <span className="text-white/60 text-sm">camere</span>
            </div>
            <p className="mt-3 text-sm text-white/55 leading-relaxed">
              {c.target_description ||
                "Con le 5 nuove aperture in pipeline tra il 2026 e il 2027, il portafoglio Invest Hospitality raggiungerà oltre 2.600 camere — consolidando la leadership tra gli operatori indipendenti milanesi."}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ label, value, prefix = "" }) {
  return (
    <div className="border-l border-white/10 pl-5">
      <NumberCounter
        value={value}
        prefix={prefix}
        className="block font-display text-4xl md:text-5xl tracking-tight leading-none"
      />
      <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
        {label}
      </div>
    </div>
  );
}
